import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WhatsappTemplatesService } from 'src/app/business-service/whatsapp-templates/whatsapp-templates.service';

@Component({
  selector: 'app-add-template',
  templateUrl: './add-template.component.html',
  styleUrls: ['./add-template.component.scss'],
})
export class AddTemplateComponent implements OnInit {
  @Output() formReady = new EventEmitter<FormGroup>();
  @Output() submitForm = new EventEmitter<any>();
  @Output() saveDraft = new EventEmitter<void>();

  templateForm!: FormGroup;

  preview = {
    image: null as SafeResourceUrl | null,
    video: null as SafeResourceUrl | null,
    document: null as SafeResourceUrl | null
  };

  documentType = '';

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private templateService: WhatsappTemplatesService
  ) { }

  ngOnInit() {
    this.templateForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-z0-9_-]+$/),
          Validators.minLength(3),
          Validators.maxLength(50)
        ]
      ],

      templateCategory: ['', Validators.required],
      language: ['', Validators.required],
      // text | image | video | document | none
      templateType: ['none', Validators.required],

      // Only used if templateType === 'text'
      headerText: [''],
      // Media file inputs
      image: [null],
      video: [null],
      document: [null],

      /* ===== BODY ===== */
      body: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(1024)
        ]
      ],

      /* ===== FOOTER ===== */
      footer: ['', Validators.maxLength(60)],

      /* ===== BUTTONS ===== */
      buttonType: ['none', Validators.required],

      quickReplies: this.fb.array([]),
      callButtons: this.fb.array([])
    });

    this.formReady.emit(this.templateForm);

    this.listenButtonType();
    this.listenTemplateType();
  }

  /* ====== FORM ARRAY GETTERS ====== */
  get quickReplies(): FormArray {
    return this.templateForm.get('quickReplies') as FormArray;
  }

  get callButtons(): FormArray {
    return this.templateForm.get('callButtons') as FormArray;
  }

  /* ====== LISTENERS ====== */
  listenButtonType() {
    this.templateForm.get('buttonType')?.valueChanges.subscribe(type => {
      this.quickReplies.clear();
      this.callButtons.clear();

      if (type === 'quick') this.addQuickReply();
      if (type === 'call') this.addCallButton();
    });
  }

  listenTemplateType() {
    this.templateForm.get('templateType')?.valueChanges.subscribe(() => {
      this.resetMedia();
    });
  }

  /* ====== BUTTON CONTROLS ====== */
  addQuickReply() {
    this.quickReplies.push(
      this.fb.group({
        text: ['', [Validators.required, Validators.maxLength(20)]]
      })
    );
  }

  removeQuickReply(i: number) {
    this.quickReplies.removeAt(i);
  }

  addCallButton() {
    this.callButtons.push(
      this.fb.group({
        actionType: ['Call Us'],
        text: ['', [Validators.required, Validators.maxLength(20)]],
        url: ['', Validators.required]
      })
    );
  }

  removeCallButton(i: number) {
    this.callButtons.removeAt(i);
  }

  createQuickReplyGroup(reply: any) {
    return this.fb.group({
      text: [reply.text, [Validators.required, Validators.maxLength(20)]]
    });
  }

  createCallButtonGroup(btn: any) {
    return this.fb.group({
      actionType: [btn.actionType || 'Call Us'],
      text: [btn.text, [Validators.required, Validators.maxLength(20)]],
      url: [btn.url, Validators.required]
    });
  }

  /* ====== FILE HANDLING ====== */
  onFileSelected(event: any, type: 'image' | 'video' | 'document') {
    const file = event.target?.files?.[0];
    if (!file) return;

    this.templateForm.patchValue({ [type]: file });

    if (type === 'document') {
      this.documentType = file.type === 'application/pdf' ? 'pdf' : 'other';

      if (this.documentType === 'other') {
        const url = URL.createObjectURL(file);
        this.preview.document =
          this.sanitizer.bypassSecurityTrustResourceUrl(url);
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.preview[type] =
        this.sanitizer.bypassSecurityTrustResourceUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  removeMedia(type: 'image' | 'video' | 'document') {
    this.templateForm.patchValue({ [type]: null });
    this.preview[type] = null;

    if (type === 'document') this.documentType = '';
  }

  reupload(type: 'image' | 'video' | 'document') {
    const input = document.createElement('input');
    input.type = 'file';

    if (type === 'image') input.accept = 'image/*';
    if (type === 'video') input.accept = 'video/*';
    if (type === 'document') input.accept = '.pdf,.doc,.docx,.txt';

    input.onchange = e => this.onFileSelected(e, type);
    input.click();
  }

  resetMedia() {
    this.preview = { image: null, video: null, document: null };
    this.documentType = '';

    this.templateForm.patchValue({
      image: null,
      video: null,
      document: null
    });
  }

  /* ====== API PAYLOAD BUILDER ====== */
  buildApiPayload(): FormData {
    const f = this.templateForm.value;
    const fd = new FormData();

    /* BASIC FIELDS */
    fd.append('name', f.name.trim().toLowerCase());
    fd.append('templateCategory', f.templateCategory);
    fd.append('language', f.language);

    const components: any[] = [];

    /* ===== HEADER ===== */
    if (f.templateType === 'text') {
      components.push({
        type: 'HEADER',
        format: 'TEXT',
        text: f.headerText
      });
    }

    if (f.templateType === 'image') {
      components.push({ type: 'HEADER', format: 'IMAGE' });
      if (f.image) fd.append('image', f.image);
    }

    if (f.templateType === 'video') {
      components.push({ type: 'HEADER', format: 'VIDEO' });
      if (f.video) fd.append('video', f.video);
    }

    if (f.templateType === 'document') {
      components.push({ type: 'HEADER', format: 'DOCUMENT' });
      if (f.document) fd.append('document', f.document);
    }

    /* ===== BODY ===== */
    components.push({
      type: 'BODY',
      text: f.body
    });

    /* ===== FOOTER ===== */
    if (f.footer?.trim()) {
      components.push({
        type: 'FOOTER',
        text: f.footer
      });
    }

    fd.append('components', JSON.stringify(components));

    /* ===== BUTTONS ===== */
    let buttons: any[] = [];

    if (f.buttonType === 'quick') {
      buttons = f.quickReplies.map((q: any) => ({
        type: 'QUICK_REPLY',
        text: q.text
      }));
    }

    if (f.buttonType === 'call') {
      buttons = f.callButtons.map((b: any) => ({
        type: 'CALL_TO_ACTION',
        text: b.text,
        url: b.url
      }));
    }

    if (buttons.length) {
      fd.append('buttons', JSON.stringify(buttons));
    }

    return fd;
  }

  /* ===== SUBMIT ===== */
  onSubmit() {
    if (this.templateForm.invalid) return;

    const payload = this.buildApiPayload();

    this.templateService.createWhatsappTemplate(payload).subscribe({
      next: res => this.submitForm.emit(res),
      error: err => console.error('API Error:', err)
    });
  }

  onSaveDraft() {
    this.saveDraft.emit();
  }
}
